<template>
  <v-data-table
    :headers="headers"
    :items="items"
    :fixed-header="true"
    :disable-pagination="true"
    :loading="$apollo.loading"
    :search="search"
    :height="tableHeight"
    :sort-by="sortBy"
    loading-text="Laddar... var god vänta!"
    no-data-text="Ingen data..."
    no-results-text="Inget resultat på din sökning..."
    hide-default-footer
    @click:row="onClickRow"
  >
    <template v-slot:top>
      <v-toolbar flat>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Sök..."
          single-line
          hide-details
          rounded
          filled
          dense
        ></v-text-field>
        <v-spacer />
        <v-switch
          v-model="showAllLostProperties"
          label="Visa alla föremål"
        ></v-switch>
      </v-toolbar>
    </template>
    <template v-slot:item.included="{ item }">
      <v-checkbox v-model="item.included" @click.stop="onClickInclude(item)" />
    </template>
    <template v-slot:item.excluded="{ item }">
      <v-checkbox
        color="warning"
        v-model="item.excluded"
        @click.stop="onClickExclude(item)"
      />
    </template>
    <template v-slot:item.matched="{ item }">
      <v-checkbox
        color="success"
        v-model="item.matched"
        @click.stop="onClickMatch(item)"
      />
    </template>
  </v-data-table>
</template>

<script>
import GET_INQUIRY from '@/gql/inquiries/queries/GetInquiry.gql'

import DELETE_INCLUDE_INQUIRY_SIMILARITY from '@/gql/inquiries/mutations/DeleteIncludeInquirySimilarity.gql'
import DELETE_EXCLUDE_INQUIRY_SIMILARITY from '@/gql/inquiries/mutations/DeleteExcludeInquirySimilarity.gql'
import DELETE_MATCH_INQUIRY_SIMILARITY from '@/gql/inquiries/mutations/DeleteMatchInquirySimilarity.gql'

import INCLUDE_INQUIRY_SIMILARITY from '@/gql/inquiries/mutations/IncludeInquirySimilarity.gql'
import EXCLUDE_INQUIRY_SIMILARITY from '@/gql/inquiries/mutations/ExcludeInquirySimilarity.gql'
import MATCH_INQUIRY_SIMILARITY from '@/gql/inquiries/mutations/MatchInquirySimilarity.gql'

import { format } from 'date-fns'
import { tableHeight } from '@/mixins/tableHeight'
import { get } from 'vuex-pathify'

export default {
  name: 'InquiriesMatches',

  mixins: [tableHeight(225)],

  data() {
    return {
      similar: [],
      showAllLostProperties: false,
      headers: [
        { text: 'Id', value: 'id' },
        { text: 'Upphittad tid', value: 'found_at' },
        { text: 'Skapad av', value: 'created_by.email' },
        { text: 'Kategori', value: 'category.category_group.name' },
        { text: 'Underkategori', value: 'category.name' },
        { text: 'Beskrivning', value: 'description' },
        { text: 'Färdsätt', value: 'transportation_type.name' },
        { text: 'Linje/tågnummer', value: 'transportation_number' },
        {
          text: 'Sträcka (till/från station eller hållplats)',
          value: 'transportation_information',
        },
        { text: 'Inkluderad', value: 'included' },
        { text: 'Exkluderad', value: 'excluded' },
        { text: 'Matchad', value: 'matched' },
      ],
      search: '',
      sortBy: 'id',
    }
  },

  apollo: {
    inquiry: {
      query: GET_INQUIRY,
      fetchPolicy: 'network-only',
      manual: true,
      result({ data }) {
        if (!data) return
        this.similar = data.inquiry.similar.map(x => ({
          excluded: x.excluded,
          included: x.included,
          matched: x.matched,
          ...x.lost_property,
          found_at: format(
            new Date(x.lost_property.found_at),
            'yyyy-MM-dd HH:mm',
          ),
        }))
      },
      variables() {
        return {
          uuid: this.$route.params.uuid,
          filter: {
            all: this.showAllLostProperties,
          },
        }
      },
    },
  },

  computed: {
    owner: get('app/owner'),

    items() {
      return this.similar.filter(x => this.showAllLostProperties || !x.excluded)
    },
  },

  methods: {
    onClickRow(v) {
      if (window.location.host.includes('localhost')) {
        window.open(`/${this.owner}/lost-properties/${v.uuid}`, '_blank')
      } else {
        window.open(
          `/backoffice/${this.owner}/lost-properties/${v.uuid}`,
          '_blank',
        )
      }
    },

    onClickInclude(item) {
      item.included = !item.included
      item.excluded = false

      if (item.included) {
        this.includeInquirySimilarity(item.uuid)
        this.deleteExcludeInquirySimilarity(item.uuid)
      } else {
        this.deleteIncludeInquirySimilarity(item.uuid)
      }
    },

    onClickExclude(item) {
      item.excluded = !item.excluded
      item.included = false

      if (item.excluded) {
        this.excludeInquirySimilarity(item.uuid)
        this.deleteIncludeInquirySimilarity(item.uuid)
      } else {
        this.deleteExcludeInquirySimilarity(item.uuid)
      }
    },

    onClickMatch(item) {
      item.matched = !item.matched

      if (item.matched) {
        this.matchInquirySimilarity(item.uuid)
      } else {
        this.deleteMatchInquirySimilarity(item.uuid)
      }
    },

    excludeInquirySimilarity(lost_property_uuid) {
      return this.$apollo.mutate({
        mutation: EXCLUDE_INQUIRY_SIMILARITY,
        variables: {
          payload: {
            inquiry_uuid: this.$route.params.uuid,
            lost_property_uuid,
          },
        },
      })
    },

    includeInquirySimilarity(lost_property_uuid) {
      return this.$apollo.mutate({
        mutation: INCLUDE_INQUIRY_SIMILARITY,
        variables: {
          payload: {
            inquiry_uuid: this.$route.params.uuid,
            lost_property_uuid,
          },
        },
      })
    },

    matchInquirySimilarity(lost_property_uuid) {
      return this.$apollo.mutate({
        mutation: MATCH_INQUIRY_SIMILARITY,
        variables: {
          payload: {
            inquiry_uuid: this.$route.params.uuid,
            lost_property_uuid,
          },
        },
      })
    },

    deleteExcludeInquirySimilarity(lost_property_uuid) {
      return this.$apollo.mutate({
        mutation: DELETE_EXCLUDE_INQUIRY_SIMILARITY,
        variables: {
          payload: {
            inquiry_uuid: this.$route.params.uuid,
            lost_property_uuid,
          },
        },
      })
    },

    deleteIncludeInquirySimilarity(lost_property_uuid) {
      return this.$apollo.mutate({
        mutation: DELETE_INCLUDE_INQUIRY_SIMILARITY,
        variables: {
          payload: {
            inquiry_uuid: this.$route.params.uuid,
            lost_property_uuid,
          },
        },
      })
    },

    deleteMatchInquirySimilarity(lost_property_uuid) {
      return this.$apollo.mutate({
        mutation: DELETE_MATCH_INQUIRY_SIMILARITY,
        variables: {
          payload: {
            inquiry_uuid: this.$route.params.uuid,
            lost_property_uuid,
          },
        },
      })
    },
  },
}
</script>
